import { useI18n } from '@ecom/i18n';
import { resolveErrorMessageKey } from '@ecom/i18n/errors';
import { Button, Field, Modal, Select } from '@ecom/ui-kit';
import { type ReactElement, useState } from 'react';

import {
  useAssignableAdminsQuery,
  useReassignMerchantsMutation,
  useTransferPortfolioMutation,
} from '../api';
import { adminName } from '../types';

import { ConfirmPasswordField } from '@/components/ui/ConfirmPasswordField';
import { useToast } from '@/components/ui/toast/ToastProvider';

/**
 * What the dialog moves: some shops (« Attribuer » / « Réattribuer », on the merchants list) or a whole portfolio (« Transférer le
 * portefeuille », on the portfolios screen).
 */
export type AssignmentTarget =
  | { readonly kind: 'merchants'; readonly tenantIds: readonly string[] }
  | {
      readonly kind: 'portfolio';
      readonly fromAdminId: string;
      readonly fromName: string;
      readonly count: number;
    };

/**
 * The superadmin's single way to move shops between administrators, drawn once.
 *
 * Three things on purpose:
 *   * the receiving administrator comes from `GET /admin/managers` - ACTIVE platform administrators only. For a transfer the giving
 *     administrator is left out of the list (the database refuses « same administrator » anyway);
 *   * a reason (10 characters at least, audited) AND the superadmin's own password again: moving shops changes who may see and
 *     decide for a business, the same ceremony as suspending one;
 *   * a refusal is shown inside the dialog with the translated message of its code and nothing else changes (the shops stay where they
 *     were). The password is cleared whatever the outcome; it is never kept.
 *
 * The screen only OFFERS this to a superadmin (a display hint); the API requires the capability, the password, and the database
 * function re-checks both.
 */
export function AssignmentDialog({
  target,
  onClose,
}: {
  readonly target: AssignmentTarget | null;
  readonly onClose: () => void;
}): ReactElement {
  const { t } = useI18n();
  const toast = useToast();
  const adminsQuery = useAssignableAdminsQuery(target !== null);
  const reassign = useReassignMerchantsMutation();
  const transfer = useTransferPortfolioMutation();

  const [toAdminId, setToAdminId] = useState('');
  const [reason, setReason] = useState('');
  const [password, setPassword] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  const admins = (adminsQuery.data ?? []).filter(
    (admin) => target?.kind !== 'portfolio' || admin.userId !== target.fromAdminId,
  );
  const busy = reassign.isPending || transfer.isPending;
  const canSubmit = toAdminId !== '' && reason.trim().length >= 10 && password.length > 0;

  function close(): void {
    setToAdminId('');
    setReason('');
    setPassword('');
    setFormError(null);
    onClose();
  }

  async function submit(): Promise<void> {
    if (target === null) return;
    setFormError(null);
    try {
      const result =
        target.kind === 'merchants'
          ? await reassign.mutateAsync({
              tenantIds: target.tenantIds,
              toAdminId,
              reason,
              confirmPassword: password,
            })
          : await transfer.mutateAsync({
              fromAdminId: target.fromAdminId,
              toAdminId,
              reason,
              confirmPassword: password,
            });
      toast.success(
        target.kind === 'merchants'
          ? t('admin.assign.success', { count: result.moved })
          : t('admin.transfer.success', { count: result.moved }),
      );
      close();
    } catch (error) {
      setPassword('');
      setFormError(t(resolveErrorMessageKey(error)));
    }
  }

  const title =
    target?.kind === 'portfolio'
      ? t('admin.transfer.title', { name: target.fromName })
      : t('admin.assign.title');
  const intro =
    target === null
      ? ''
      : target.kind === 'portfolio'
        ? t('admin.transfer.intro', { count: target.count })
        : t('admin.assign.intro', { count: target.tenantIds.length });

  return (
    <Modal
      open={target !== null}
      title={title}
      onClose={close}
      footer={
        <>
          <Button variant="secondary" onClick={close}>
            {t('merchant.common.cancel')}
          </Button>
          <Button
            onClick={() => {
              void submit();
            }}
            loading={busy}
            disabled={!canSubmit}
          >
            {target?.kind === 'portfolio' ? t('admin.transfer.confirm') : t('admin.assign.confirm')}
          </Button>
        </>
      }
    >
      {formError !== null ? (
        <p role="alert" className="mb-3 rounded-md bg-criticalSoft px-3 py-2 text-sm text-critical">
          {formError}
        </p>
      ) : null}
      <p className="mb-3 rounded-md bg-surface2 px-3 py-2 text-sm text-inkSoft">{intro}</p>

      <div className="flex flex-col gap-4">
        <Field
          label={
            target?.kind === 'portfolio'
              ? t('admin.transfer.targetLabel')
              : t('admin.assign.adminLabel')
          }
        >
          {(bind) => (
            <Select
              {...bind}
              value={toAdminId}
              onChange={(event) => {
                setToAdminId(event.target.value);
              }}
              placeholder={t('admin.assign.adminPlaceholder')}
              options={admins.map((admin) => ({
                value: admin.userId,
                label: `${adminName(admin)} — ${admin.email}`,
              }))}
            />
          )}
        </Field>
        {adminsQuery.isSuccess && admins.length === 0 ? (
          <p className="text-sm text-inkFaint">{t('admin.assign.noAdmins')}</p>
        ) : null}

        <Field label={t('admin.common.reasonLabel')}>
          {(bind) => (
            <textarea
              {...bind}
              value={reason}
              onChange={(event) => {
                setReason(event.target.value);
              }}
              rows={3}
              maxLength={500}
              className="ecom-input"
            />
          )}
        </Field>

        <ConfirmPasswordField value={password} onChange={setPassword} />
      </div>
    </Modal>
  );
}
